import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { ICurrentWorkspaceStore } from '../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { delay } from '../helpers/function.helpers';
import { ACDLError } from '../models/error.model';
import { CacheManager, ICacheManager } from '../services/cacheManager';
import { ICryptoService } from '../services/crypto.service';
import { Loader } from './Loader';

const RETRY_NUMBER: number = 30;

interface IProps {
  source: string;
  headers?: Headers;
}

export const LazyImage = observer((props: IProps): ReactElement => {
  const store = useInjection(ICurrentWorkspaceStore.$);
  const cryptoService = useInjection(ICryptoService.$);
  const cacheManager = useInjection(ICacheManager.$);

  const [image, setImage] = useState<string | undefined>(undefined);
  const [retryCount, setRetryCount] = useState<number>(0);
  const isComponentMounted = useRef<boolean>(true);
  const currentTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    void getImage();

    return (): void => {
      if (image) {
        URL.revokeObjectURL(image);
      }
      if (currentTimeout.current) {
        clearTimeout(currentTimeout.current);
      }
      isComponentMounted.current = false;
    };
  }, []);

  const getImage = async (): Promise<void> => {
    if (store.currentWorkspace.encryption.encrypted) {
      // eslint-disable-next-line no-await-in-loop
      while (!(await cryptoService.checkKey(store.currentWorkspace.id, store.currentWorkspace.id))) {
        // eslint-disable-next-line no-await-in-loop
        await delay(1000);
      }
    }

    const blob = await fetchImage();

    if (!isComponentMounted.current) {
      return;
    }

    if (blob !== undefined) {
      const image = window.URL.createObjectURL(blob);
      setImage(image);
    } else {
      if (retryCount <= RETRY_NUMBER) {
        setRetryCount(retryCount + 1);
        currentTimeout.current = setTimeout(() => {
          void getImage();
        }, retryCount * 1000);
      }
    }
  };

  const fetchImage = async (): Promise<Blob | undefined> => {
    const cacheAvailable = await CacheManager.checkCacheAvailabilityAsync();

    if (cacheAvailable) {
      const cachedArrayBuffer = await cacheManager.tryGetFromCacheAsync(props.source);
      if (cachedArrayBuffer) {
        return new Blob([cachedArrayBuffer]);
      }
    }

    const response = await fetch(props.source, { headers: props.headers });

    if (response instanceof ACDLError || response.status !== 200) {
      return undefined;
    }

    let arrayBufferResponse = await response.arrayBuffer();

    arrayBufferResponse = await cryptoService.decrypt(store.currentWorkspace.id, arrayBufferResponse);

    if (cacheAvailable) {
      try {
        await cacheManager.makeRoomInCacheAsync(arrayBufferResponse.byteLength);
        await cacheManager.putInCacheAsync(props.source, arrayBufferResponse);
      } catch {
        // caching failed but the image can still be shown
      }
    }

    return new Blob([arrayBufferResponse]);
  };

  return (
    <Loader isLoading={image === undefined}>
      <img
        className='lazy-image'
        src={image}
      />
    </Loader>
  );
});
